/**
 * Search Filter Bar Component
 * Search input with category dropdown filter
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../hooks/useTranslation';

interface Category {
  id: number | string;
  name: string;
}

interface SearchFilterBarProps {
  categories: Category[];
  selectedCategory: number | string | null;
  onCategoryChange: (categoryId: number | string | null) => void;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  onSearch,
  searchPlaceholder
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const placeholder = searchPlaceholder || t('common.searchProducts');

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, onSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onCategoryChange(value === 'all' ? null : value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch('');
  };

  return (
    <FilterBarContainer>
      <SearchContainer>
        <SearchIcon>🔍</SearchIcon>
        <SearchInput
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {searchQuery && (
          <ClearButton onClick={clearSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </ClearButton>
        )}
      </SearchContainer>

      <CategoryFilter>
        <CategorySelect
          value={selectedCategory?.toString() || 'all'}
          onChange={handleCategoryChange}
        >
          <option value="all">{t('shop.allCategories')}</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id.toString()}>
              {category.name}
            </option>
          ))}
        </CategorySelect>
        <SelectArrow>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </SelectArrow>
      </CategoryFilter>
    </FilterBarContainer>
  );
};

const FilterBarContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: var(--tg-theme-bg-color, #1a1a1a);
  align-items: center;

  @media (max-width: 380px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 380px) {
    width: 100%;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--tg-theme-hint-color, #8e8e93);
  font-size: 14px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 36px 12px 40px;
  border: none;
  border-radius: 12px;
  background: var(--tg-theme-secondary-bg-color, #2c2c2e);
  color: var(--tg-theme-text-color, #ffffff);
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: var(--tg-theme-hint-color, #8e8e93);
  }

  &:focus {
    box-shadow: 0 0 0 2px var(--tg-theme-button-color, #3390ec);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: var(--tg-theme-hint-color, #48484a);
  color: var(--tg-theme-bg-color, #1a1a1a);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const CategoryFilter = styled.div`
  position: relative;
  min-width: 120px;

  @media (max-width: 380px) {
    width: 100%;
    min-width: 100%;
  }
`;

const CategorySelect = styled.select`
  width: 100%;
  padding: 12px 30px 12px 12px;
  border: none;
  border-radius: 12px;
  background: var(--tg-theme-secondary-bg-color, #2c2c2e);
  color: var(--tg-theme-text-color, #ffffff);
  font-size: 14px;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    box-shadow: 0 0 0 2px var(--tg-theme-button-color, #3390ec);
  }

  option {
    background: var(--tg-theme-bg-color, #1a1a1a);
    color: var(--tg-theme-text-color, #ffffff);
  }
`;

const SelectArrow = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--tg-theme-hint-color, #8e8e93);
  pointer-events: none;
`;

export default SearchFilterBar;
